import { lazy, type ComponentType } from "react";
import { safeSessionStorage } from "./safeStorage";

/**
 * Drop-in replacement for React.lazy that auto-recovers from stale code-split
 * chunks after a deploy.
 *
 * A tab opened before a deploy holds the OLD chunk hashes (e.g. Method-DlmS7aMg.js).
 * After a new build the server only has the NEW hashes, so the old chunk 404s and
 * the dynamic import rejects with "Failed to fetch dynamically imported module".
 * Reloading pulls a fresh index.html with the current hashes and fixes it.
 *
 * Here we do that reload automatically: on a failed import we force ONE hard
 * reload, guarded by a sessionStorage flag so a genuinely-missing chunk can't
 * loop. The user sees a brief refresh instead of an error screen.
 *
 * The flag goes through safeSessionStorage: where storage is blocked (in-app
 * webviews, sandboxed frames, site data turned off) touching sessionStorage
 * throws a SecurityError, and doing that here would take down every lazy route
 * on the site — the guard is a nicety, the page is not.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    const FLAG = "chunk-reload-attempted";
    try {
      const mod = await importer();
      // Successful load — clear the guard so future stale chunks can reload again.
      safeSessionStorage.removeItem(FLAG);
      return mod;
    } catch (err) {
      const alreadyReloaded = safeSessionStorage.getItem(FLAG);
      // With no storage to remember the attempt we'd reload forever, so only
      // auto-reload when the flag can actually be written.
      if (!alreadyReloaded && safeSessionStorage.setItem(FLAG, "1")) {
        window.location.reload();
        // Return a never-resolving promise so React shows the Suspense fallback
        // (not the error boundary) during the split-second before reload.
        return new Promise<{ default: T }>(() => {});
      }
      // Already reloaded once and it still failed — the chunk is genuinely
      // gone/broken. Let the error boundary handle it.
      throw err;
    }
  });
}
